/**
 * Example: How to integrate the modular visualizer system
 * 
 * This file demonstrates how to replace the monolithic visualizer code
 * in MasterOutput.tsx with the new modular system.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Wave } from '../../../types';
import { 
  getVisualizerRegistry, 
  ViewMode, 
  VisualizerConfig,
  IVisualizer,
  ColorMode
} from '../visualizers';

/**
 * Helper function to get channel-based color
 * Centralized color mapping for consistency
 */
const getChannelColor = (channel?: string | number): string => {
  if (channel === 'left' || channel === 1) return '#3b82f6'; // Blue
  if (channel === 'right' || channel === 2) return '#ec4899'; // Pink
  return '#10B981'; // Green (both/center)
};

/**
 * Convert raw audio frequency data to Wave objects
 * This normalization happens once at the renderer level, not in each visualizer
 */
const generateWavesFromAudioData = (audioData: Float32Array): Wave[] => {
  const waves: Wave[] = [];
  const maxWaves = 8; // Limit to top 8 frequencies for performance
  const freqAmpPairs: Array<{ freq: number; amp: number; channel: number }> = [];
  
  // Parse audio data (freq, amp, channel triplets)
  for (let i = 0; i < audioData.length && i < maxWaves * 3 * 10; i += 3) {
    if (i + 2 < audioData.length) {
      const freq = audioData[i];
      const amp = audioData[i + 1];
      const channel = audioData[i + 2];
      
      if (amp > 0.01 && freq >= 20 && freq <= 20000) {
        freqAmpPairs.push({ freq, amp, channel });
      }
    }
  }
  
  // Sort by amplitude and take top frequencies
  freqAmpPairs.sort((a, b) => b.amp - a.amp);
  const topFreqs = freqAmpPairs.slice(0, maxWaves);
  
  // Convert to Wave objects
  topFreqs.forEach((item, index) => {
    const channelName = item.channel === 1 ? 'left' : item.channel === 2 ? 'right' : 'both';
    const color = getChannelColor(channelName);
    
    waves.push({
      id: index,
      freq: item.freq,
      amp: item.amp * 100,
      phase: 0,
      waveform: 'sine',
      color,
      muted: false,
      channel: channelName as any
    });
  });
  
  return waves;
};

interface VisualizerRendererProps {
  viewMode: ViewMode;
  waves: Wave[];
  speed: number;
  uploadedAudioData?: Float32Array | null;
  waveformData?: { left: Float32Array; right: Float32Array } | null;
  isUploadedAudioPlaying?: boolean;
  width: number;
  height: number;
  zoom: number;
  pan: { x: number; y: number };
  isAutoRotating?: boolean;
  autoRotRef?: number;
  smoothedMusicData?: {
    bassEnergy: number;
    midEnergy: number;
    trebleEnergy: number;
    totalEnergy: number;
  } | null;
  mousePos?: { x: number; y: number } | null;
  colorMode?: ColorMode;
  mergeChannels?: boolean;
  onAutoRotUpdate?: (newValue: number) => void;
  onHoveredFrequency?: (freq: { freq: number; amp: number; channel: string; color: string } | null) => void;
}

/**
 * Modular Visualizer Renderer Component
 * 
 * This component replaces the massive switch statement in MasterOutput.tsx
 * with a dynamic, registry-based approach.
 */
export const VisualizerRenderer: React.FC<VisualizerRendererProps> = ({
  viewMode,
  waves,
  speed,
  uploadedAudioData,
  waveformData,
  isUploadedAudioPlaying,
  width,
  height,
  zoom,
  pan,
  isAutoRotating,
  autoRotRef = 0,
  smoothedMusicData,
  mousePos,
  colorMode = 'wave',
  mergeChannels = false,
  onAutoRotUpdate,
  onHoveredFrequency,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timeRef = useRef(0);
  const lastFrameTime = useRef(performance.now());
  const animationFrameRef = useRef<number | undefined>(undefined);

  // Get the current visualizer from registry
  const registry = getVisualizerRegistry();
  const currentVisualizer: IVisualizer | undefined = registry.get(viewMode);

  /**
   * Data Normalization Layer
   * Convert music data to synthetic waves when playing, otherwise use frequency layers
   * This ensures visualizers only receive one unified data format: activeWaves
   */
  const activeWaves = React.useMemo(() => {
    // If music is playing with audio data, use it (replaces frequency layers)
    if (isUploadedAudioPlaying && uploadedAudioData && uploadedAudioData.length > 0) {
      return generateWavesFromAudioData(uploadedAudioData);
    }
    
    // Otherwise use frequency layers (filtered for active, non-muted waves)
    return waves.filter(w => !w.muted && w.amp > 0);
  }, [waves, isUploadedAudioPlaying, uploadedAudioData]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Check if visualizer uses WebGL
    const usesWebGL = currentVisualizer?.metadata?.usesWebGL ?? false;

    // Only get 2D context for non-WebGL visualizers
    // Note: Canvas is recreated (via key) when switching between WebGL/2D, so no context conflicts
    const ctx = usesWebGL ? null : canvas.getContext('2d');
    if (!usesWebGL && !ctx) return;

    // Animation loop
    const animate = () => {
      const now = performance.now();
      const deltaTime = (now - lastFrameTime.current) / 1000;
      lastFrameTime.current = now;
      timeRef.current += deltaTime * speed;

      if (!currentVisualizer) {
        // Fallback: Draw error message (only if we have a 2D context)
        if (ctx) {
          ctx.fillStyle = '#000000';
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = '#ff0000';
          ctx.font = '16px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(
            `Visualizer "${viewMode}" not found`,
            width / 2,
            height / 2
          );
        }
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      // Prepare config for visualizer
      const config: VisualizerConfig = {
        ctx,
        canvas,
        width,
        height,
        waves,
        activeWaves,
        speed,
        time: timeRef.current,
        deltaTime,
        uploadedAudioData,
        waveformData,
        isUploadedAudioPlaying,
        zoom,
        pan,
        isAutoRotating,
        autoRotRef,
        smoothedMusicData,
        mousePos,
        colorMode,
        mergeChannels,
        setHoveredFrequency: onHoveredFrequency,
      };

      // Render the visualizer
      const result = currentVisualizer.render(config);

      // Handle auto-rotation updates for 3D visualizers
      if (typeof result === 'number' && onAutoRotUpdate) {
        onAutoRotUpdate(result);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [
    viewMode,
    waves,
    speed,
    uploadedAudioData,
    waveformData,
    isUploadedAudioPlaying,
    width,
    height,
    zoom,
    pan,
    isAutoRotating,
    autoRotRef,
    smoothedMusicData,
    mousePos,
    currentVisualizer,
    activeWaves,
    onAutoRotUpdate,
    onHoveredFrequency,
  ]);

  // Call lifecycle hooks when visualizer changes
  useEffect(() => {
    if (currentVisualizer?.onActivate) {
      currentVisualizer.onActivate();
    }

    return () => {
      if (currentVisualizer?.onDeactivate) {
        currentVisualizer.onDeactivate();
      }
    };
  }, [currentVisualizer]);

  // Generate a key that changes when switching between WebGL and non-WebGL visualizers
  // This forces React to create a new canvas element, preventing context conflicts
  const canvasKey = `canvas-${viewMode}-${currentVisualizer?.metadata?.usesWebGL ? 'webgl' : '2d'}`;

  return (
    <canvas
      key={canvasKey}
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
};

/**
 * Example usage in MasterOutput.tsx:
 * 
 * Replace the massive canvas rendering code with:
 * 
 * ```tsx
 * import { VisualizerRenderer } from './visualizers/VisualizerRenderer';
 * 
 * // In your component:
 * <VisualizerRenderer
 *   viewMode={viewMode}
 *   waves={waves}
 *   speed={speed}
 *   uploadedAudioData={uploadedAudioData}
 *   waveformData={waveformData}
 *   isUploadedAudioPlaying={isUploadedAudioPlaying}
 *   width={containerWidth}
 *   height={height}
 *   zoom={viewSettings[viewMode].zoom}
 *   pan={viewSettings[viewMode].pan}
 *   isAutoRotating={isAutoRotating}
 *   autoRotRef={autoRotRef.current}
 *   smoothedMusicData={smoothedMusicDataRef.current}
 *   mousePos={mousePos}
 *   onAutoRotUpdate={(newValue) => { autoRotRef.current = newValue; }}
 *   onHoveredFrequency={setHoveredFrequency}
 * />
 * ```
 */

/**
 * Another example: Visualizer Selector UI
 */
export const VisualizerSelector: React.FC<{
  currentMode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
}> = ({ currentMode, onModeChange }) => {
  const registry = getVisualizerRegistry();
  const allVisualizers = registry.getAll();

  // Group by category
  const byCategory = allVisualizers.reduce((acc, viz) => {
    const category = viz.metadata.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(viz);
    return acc;
  }, {} as Record<string, IVisualizer[]>);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {Object.entries(byCategory).map(([category, visualizers]) => (
        <div key={category}>
          <h3 style={{ textTransform: 'capitalize', marginBottom: '0.5rem' }}>
            {category}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {visualizers.map((viz) => (
              <button
                key={viz.metadata.id}
                onClick={() => onModeChange(viz.metadata.id)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: currentMode === viz.metadata.id ? '#3b82f6' : '#1f2937',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              >
                <div style={{ fontWeight: 'bold' }}>{viz.metadata.name}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                  {viz.metadata.description}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
